import React, { useState, useEffect } from 'react';
import { AnimatePresence } from 'framer-motion';
import './i18n';
import './styles/performance.css';
import Navigation from './components/Navigation';
import OptimizedHero from './components/OptimizedHero';
import SEOOptimized from './components/SEOOptimized';
import SectionSelector from './components/SectionSelector';
import BookingWidget from './components/BookingWidget';
import About from './components/About';
import Rooms from './components/Rooms';
import Gallery from './components/Gallery';
import Amenities from './components/Amenities';
import RestaurantMenu from './components/RestaurantMenu';
import RestoranVideo from './components/RestoranVideo';
import RestaurantGallery from './components/RestaurantGalleryScroll';
import Testimonials from './components/Testimonials';
import Footer from './components/Footer';
import Contact from './components/Contact';
import Impressum from './components/Impressum';
import Datenschutz from './components/Datenschutz';
import CookieBanner from './components/CookieBanner';
import LoadingScreen from './components/LoadingScreen';
import CookieSettingsButton from './components/CookieSettingsButton';
import usePerformanceOptimizer from './hooks/usePerformanceOptimizer';
import { useAccessibility } from './hooks/useAccessibility';
import { initPerformanceMonitoring, monitorNetworkInfo } from './utils/webVitalsOptimized';

function App() {
  // Performance optimizations
  usePerformanceOptimizer();
  
  // Accessibility optimizations
  useAccessibility();
  
  // Initialize performance monitoring for Lighthouse 100/100
  useEffect(() => {
    initPerformanceMonitoring();
    monitorNetworkInfo();
  }, []);
  
  const [language, setLanguage] = useState('de'); // Default German
  const [activeSection, setActiveSection] = useState('hotel');
  const [isBookingOpen, setIsBookingOpen] = useState(false);
  const [bookingType, setBookingType] = useState('room');
  const [currentPage, setCurrentPage] = useState('home');
  const [isLoading, setIsLoading] = useState(true);
  const [showCookieBanner, setShowCookieBanner] = useState(false);

  const openBooking = (type = 'room') => {
    setBookingType(type);
    setIsBookingOpen(true);
    // Scroll to booking widget
    setTimeout(() => {
      document.getElementById('booking')?.scrollIntoView({ behavior: 'smooth' });
    }, 100);
  };

  // Loading screen - minimum 1 second
  useEffect(() => {
    const timer = setTimeout(() => {
      setIsLoading(false);
    }, 1000);

    return () => clearTimeout(timer);
  }, []);

  // Simple routing based on URL hash
  useEffect(() => {
    const handleRouteChange = () => {
      const path = window.location.pathname;
      if (path === '/impressum') {
        setCurrentPage('impressum');
      } else if (path === '/datenschutz') {
        setCurrentPage('datenschutz');
      } else {
        setCurrentPage('home');
      }
    };

    handleRouteChange();
    window.addEventListener('popstate', handleRouteChange);
    
    // Handle link clicks
    const handleLinkClick = (e) => {
      const target = e.target.closest('a');
      if (target && (target.getAttribute('href') === '/impressum' || target.getAttribute('href') === '/datenschutz')) {
        e.preventDefault();
        window.history.pushState({}, '', target.getAttribute('href'));
        handleRouteChange();
        window.scrollTo(0, 0);
      }
    };

    document.addEventListener('click', handleLinkClick);

    return () => {
      window.removeEventListener('popstate', handleRouteChange);
      document.removeEventListener('click', handleLinkClick);
    };
  }, []);

  // Render Impressum page - NO NAVIGATION, NO FOOTER
  if (currentPage === 'impressum') {
    return (
      <>
        <AnimatePresence>
          <LoadingScreen isLoading={isLoading} />
        </AnimatePresence>
        {!isLoading && (
          <div className="App overflow-x-hidden">
            <Impressum />
            <CookieBanner forceShow={showCookieBanner} onClose={() => setShowCookieBanner(false)} />
            <CookieSettingsButton onClick={() => setShowCookieBanner(true)} />
          </div>
        )}
      </>
    );
  }

  // Render Datenschutz page - NO NAVIGATION, NO FOOTER
  if (currentPage === 'datenschutz') {
    return (
      <>
        <AnimatePresence>
          <LoadingScreen isLoading={isLoading} />
        </AnimatePresence>
        {!isLoading && (
          <div className="App overflow-x-hidden">
            <Datenschutz />
            <CookieBanner forceShow={showCookieBanner} onClose={() => setShowCookieBanner(false)} />
            <CookieSettingsButton onClick={() => setShowCookieBanner(true)} />
          </div>
        )}
      </>
    );
  }

  // Render Contact page
  if (currentPage === 'contact') {
    return (
      <>
        <AnimatePresence>
          <LoadingScreen isLoading={isLoading} />
        </AnimatePresence>
        {!isLoading && (
          <div className="App overflow-x-hidden">
            <SEOOptimized />
            <Contact language={language} setCurrentPage={setCurrentPage} />
            <CookieBanner language={language} forceShow={showCookieBanner} onClose={() => setShowCookieBanner(false)} />
            <CookieSettingsButton language={language} onClick={() => setShowCookieBanner(true)} />
          </div>
        )}
      </>
    );
  }

  // Render Home page
  return (
    <>
      <AnimatePresence>
        <LoadingScreen isLoading={isLoading} />
      </AnimatePresence>
      {!isLoading && (
        <div className="App overflow-x-hidden">
          <SEOOptimized />
          <Navigation 
            language={language}
            setLanguage={setLanguage}
            activeSection={activeSection} 
            setActiveSection={setActiveSection}
            openBooking={openBooking}
            currentPage={currentPage}
            setCurrentPage={setCurrentPage}
          />
          <OptimizedHero 
            language={language}
            activeSection={activeSection} 
            openBooking={openBooking} 
          />
          
          
          
          <BookingWidget 
            language={language}
            isOpen={isBookingOpen}
            onClose={() => setIsBookingOpen(false)}
            initialBookingType={bookingType}
          />
          
          {/* Hotel Section */}
          {activeSection === 'hotel' && (
            <>
              <About language={language} openBooking={openBooking} activeSection="hotel" />
              <Rooms language={language} openBooking={openBooking} setCurrentPage={setCurrentPage} />
              <Amenities language={language} activeSection={activeSection} />
              <Gallery language={language} section="hotel" />
            </>
          )}
          
          {/* Restaurant Section */}
          {activeSection === 'restaurant' && (
            <>
              <About language={language} openBooking={openBooking} activeSection="restaurant" />
              <RestoranVideo language={language} />
              <RestaurantMenu language={language} />
              <Amenities language={language} activeSection={activeSection} />
              <RestaurantGallery language={language} />
            </>
          )}
          
          <Testimonials language={language} />
          <Footer language={language} setCurrentPage={setCurrentPage} />
          <CookieBanner language={language} forceShow={showCookieBanner} onClose={() => setShowCookieBanner(false)} />
          <CookieSettingsButton language={language} onClick={() => setShowCookieBanner(true)} />
        </div>
      )}
    </>
  );
}

export default App;
